import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, Ear, Users, ArrowRight } from "lucide-react";

const steps = [
  "read the book",
  "enter the space",
  "listen to Maria Gioia",
  "listen to other testimonies",
  "recognise yourself",
  "share your story",
  "take part",
  "go deeper",
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background grain">
      <header className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <span className="font-heading text-xl sm:text-2xl uppercase tracking-[0.18em]">Self Doubling<span className="text-primary">©</span></span>
        <Button variant="ghost" className="rounded-full uppercase tracking-[0.14em] text-xs" onClick={() => navigate("/accedi")} data-testid="header-login-btn">
          Sign in
        </Button>
      </header>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-20 pb-20 grid lg:grid-cols-2 gap-14 items-center">
        <div className="animate-fade-in-up">
          <p className="eyebrow mb-6">∞ · A space of listening · Mosca Method</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-foreground">
            A human connective tissue, <span className="gold-text italic">healthy and organic.</span>
          </h1>
          <p className="mt-7 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
            Not a social network, but a space where those who have read the book can share parts of themselves,
            listen, recognise one another and feel heard. A place that always remains a listening ear for whoever wishes to be heard.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button className="btn-gold rounded-full h-12 px-8 text-sm font-medium" onClick={() => navigate("/request-access")} data-testid="request-access-btn">
              Request access <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" className="rounded-full h-12 px-8 border-primary/50 text-foreground hover:bg-primary/10 uppercase tracking-[0.14em] text-sm bg-transparent" onClick={() => navigate("/accedi")} data-testid="landing-login-btn">
              I already have access
            </Button>
          </div>
          <p className="mt-5 text-sm text-muted-foreground">
            Access is reserved to those who own the book, in the Italian or English edition.
          </p>
        </div>

        <div className="animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative rounded-2xl overflow-hidden border border-primary/25">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/cf332f2b-3e53-4f2e-bb76-65bd87721435/images/3aeb997102ca3b32b89251505551fa1aeae6c8863d2365c423ff3dcb88b800b0.jpeg"
              alt="A woman holding the Self Doubling book"
              className="w-full h-[540px] object-cover object-center brightness-[0.9]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/25 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="hairline mb-4" />
              <p className="font-heading italic text-2xl text-foreground/90">"This is the deepest embrace you can give yourself."</p>
            </div>
          </div>
        </div>
      </section>

      <div className="relative overflow-hidden border-y border-border py-5 bg-background/40">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center shrink-0">
              {["Psychodrama", "Trauma", "Embodied Memory", "The Double", "Self Doubling", "Transgenerational"].map((w) => (
                <span key={w} className="flex items-center">
                  <span className="font-heading italic text-2xl sm:text-3xl text-muted-foreground/70 px-8">{w}</span>
                  <span className="text-primary/60">∞</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <section className="bg-secondary/60 border-y border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-3 gap-8">
          {[
            { icon: Ear, t: "Listening", d: "Podcasts, audio and reflections by Maria Gioia become a starting point for looking within." },
            { icon: Users, t: "Recognition", d: "Testimonies have no likes and no followers. Only people who recognise one another for what they live." },
            { icon: BookOpen, t: "Gradualness", d: "No rush to sell a path: first familiarity, listening, trust. Then, if you wish, going deeper." },
          ].map((c) => (
            <div key={c.t} className="bg-card rounded-3xl p-8 border border-border">
              <c.icon className="w-7 h-7 text-primary mb-5" />
              <h3 className="font-heading text-2xl mb-3">{c.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight mb-10">A path, one step at a time</h2>
        <div className="flex flex-wrap justify-center gap-3">
          {steps.map((s, i) => (
            <span key={s} className="inline-flex items-center gap-2 bg-card border border-border rounded-full px-5 py-2 text-sm text-muted-foreground">
              <span className="text-primary font-medium">{i + 1}</span> {s}
            </span>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-10 text-center text-sm text-muted-foreground">
        <p className="mb-2">Self Doubling© – Mosca Method · A protected space, respectful of your vulnerability.</p>
        <p>© {new Date().getFullYear()} Self Doubling© – Mosca Method. All rights reserved. Content is copyright protected.</p>
      </footer>
    </div>
  );
}
